import { App } from "antd"
import React, { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

import { getPermissionValueAtPath } from "../../../components/PermissionGate/PermissionGate.tsx"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import {
  firstErrorMessage,
  removeEnvelope,
  resendEnvelope
} from "../api/esign.services.ts"
import { VoidRequestModal } from "../components/VoidRequestModal.tsx"
import { joinNames } from "../helpers/join-names.ts"
import type { EnvelopeStatus } from "../types/esign.types.ts"
import {
  CREATE_REQUEST_PERMISSION,
  esignRight
} from "./use-send-for-signature.ts"

export const VOID_PERMISSION = "esign.signatures.document_void" as const
export const RESEND_PERMISSION = "esign.features.esign_resend" as const

const WAITING: EnvelopeStatus[] = ["sent", "partially_signed"]
// Signed but not yet sealed can still be withdrawn; completed never can.
const VOIDABLE: EnvelopeStatus[] = [...WAITING, "signed"]

interface Voiding {
  id: string
  documentName: string
}

/** Resend, Void and Delete for one request, gated on the rights each needs; `onChanged` reloads the caller's list. */
export const useEnvelopeLifecycle = (onChanged: () => void) => {
  const { t, i18n } = useTranslation()
  const { message, modal } = App.useApp()
  const codes = useAppSelector((state) => state.auth?.permissions?.codes)
  const canResend = esignRight(codes, RESEND_PERMISSION)
  const canRaise = esignRight(codes, CREATE_REQUEST_PERMISSION)
  const canVoid = getPermissionValueAtPath(codes, VOID_PERMISSION)
  const [voiding, setVoiding] = useState<Voiding | null>(null)
  const [resending, setResending] = useState<string | null>(null)

  const mayResend = (status: EnvelopeStatus) =>
    canResend && WAITING.includes(status)
  const mayVoid = (status: EnvelopeStatus) =>
    !!canVoid && VOIDABLE.includes(status)
  const mayDelete = (status: EnvelopeStatus) => !!canVoid && status === "voided"

  const startDelete = useCallback(
    (id: string, documentName: string) => {
      modal.confirm({
        title: t("esign.lifecycle.deleteConfirmTitle", {
          document: documentName
        }),
        content: t("esign.lifecycle.deleteConfirmBody"),
        okText: t("esign.lifecycle.delete"),
        okButtonProps: { danger: true },
        cancelText: t("esign.common.cancel"),
        onOk: async () => {
          const result = await removeEnvelope(id)
          if (!result.ok) {
            message.error(
              firstErrorMessage(result) ?? t("esign.lifecycle.deleteFailed")
            )
            return
          }
          message.success(t("esign.lifecycle.deleted"))
          onChanged()
        }
      })
    },
    [message, modal, onChanged, t]
  )

  const resend = useCallback(
    async (id: string) => {
      setResending(id)
      const result = await resendEnvelope(id)
      setResending(null)
      if (!result.ok) {
        message.error(
          firstErrorMessage(result) ?? t("esign.lifecycle.resendFailed")
        )
        return
      }
      const names = (result.data?.recipients ?? []).map((recipient) =>
        recipient.is_countersigner
          ? t("esign.panel.yourCompany")
          : recipient.name
      )
      message.success(
        t("esign.lifecycle.resent", { names: joinNames(names, i18n.language) })
      )
      onChanged()
    },
    [i18n.language, message, onChanged, t]
  )

  const dialog = voiding ? (
    <VoidRequestModal
      envelopeId={voiding.id}
      documentName={voiding.documentName}
      onCancel={() => setVoiding(null)}
      onVoided={() => {
        setVoiding(null)
        message.success(t("esign.lifecycle.voided"))
        onChanged()
      }}
    />
  ) : null

  return {
    /** Whether the caller may raise a new request at all. */
    mayRaise: canRaise,
    mayResend,
    mayVoid,
    resend,
    resending,
    startVoid: (id: string, documentName: string) =>
      setVoiding({ id, documentName }),
    mayDelete,
    startDelete,
    dialog
  }
}
